'use client';

import { motion, useMotionValue, useScroll } from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';

import { useBackgroundColor } from '@/contexts/BackgroundColorContext';
import {
  COLOR_MAP,
  interpolateRgb,
  parseHexToRgb,
} from '@/utils/colorFunctions';

import styles from './BackgroundGradient.module.scss';

type BlockData = {
  start: number;
  end: number;
  isHero: boolean;
  color: string | null;
};

type BackgroundGradientProps = {
  children: React.ReactNode;
};

export const BackgroundGradient: React.FC<BackgroundGradientProps> = ({
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [blocks, setBlocks] = useState<BlockData[]>([]);
  const { setBackgroundColor } = useBackgroundColor();
  const { scrollY } = useScroll();

  const backgroundImage = useMotionValue(
    'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
  );

  const FADE_OFFSET = 200;

  // 1) Collect block positions
  useEffect(() => {
    requestAnimationFrame(() => {
      if (!containerRef.current) return;

      const blockEls = Array.from(
        containerRef.current.querySelectorAll('[data-background-color]')
      ) as HTMLElement[];

      const newBlocks: BlockData[] = blockEls.map((el) => {
        const rect = el.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        const bottom = top + rect.height;

        return {
          start: top,
          end: bottom,
          isHero: el.dataset.hero === 'true',
          color: el.dataset.backgroundColor || null,
        };
      });

      setBlocks(newBlocks);
    });
  }, []);

  // 2) On scroll, compute gradient + set backgroundColor (excluding hero blocks)
  useEffect(() => {
    const unsubscribe = scrollY.on('change', (currentY) => {
      const activeBlocks = blocks.filter((b) => !b.isHero);
      if (!activeBlocks.length) return;

      // Find previous block
      let prevIndex = 0;
      for (let i = 0; i < activeBlocks.length; i++) {
        if (activeBlocks[i].start <= currentY) {
          prevIndex = i;
        }
      }
      prevIndex = Math.min(prevIndex, activeBlocks.length - 1);
      const nextIndex = Math.min(prevIndex + 1, activeBlocks.length - 1);

      const blockA = activeBlocks[prevIndex];
      const blockB = activeBlocks[nextIndex];

      const fadeStart = blockA.start;
      const fadeEnd = blockB.start - FADE_OFFSET;

      // Before fade range → use blockA
      if (currentY < fadeStart) {
        const cHex = COLOR_MAP[blockA.color || 'white'] || '#ffffff';
        backgroundImage.set(
          `linear-gradient(to bottom, ${cHex} 0%, ${cHex} 100%)`
        );
        setBackgroundColor(cHex);
        return;
      }

      // After fade range → use blockB
      if (currentY >= fadeEnd) {
        const cHex = COLOR_MAP[blockB.color || 'white'] || '#ffffff';
        backgroundImage.set(
          `linear-gradient(to bottom, ${cHex} 0%, ${cHex} 100%)`
        );
        setBackgroundColor(cHex);
        return;
      }

      // Interpolate between A and B
      const distance = fadeEnd - fadeStart;
      const t = distance === 0 ? 0 : (currentY - fadeStart) / distance;
      const clamped = Math.max(0, Math.min(1, t));

      const colorAHex = COLOR_MAP[blockA.color || 'white'] || '#ffffff';
      const colorBHex = COLOR_MAP[blockB.color || 'white'] || '#ffffff';
      const rgbA = parseHexToRgb(colorAHex);
      const rgbB = parseHexToRgb(colorBHex);
      const [r, g, b] = interpolateRgb(rgbA, rgbB, clamped);
      const blended = `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;

      backgroundImage.set(
        `linear-gradient(to bottom, ${blended} 0%, ${blended} 100%)`
      );
      setBackgroundColor(blended);
    });

    return () => unsubscribe();
  }, [blocks, scrollY, backgroundImage, setBackgroundColor]);

  return (
    <div ref={containerRef} className={styles.wrapper}>
      <motion.div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          backgroundImage,
          willChange: 'background-image',
        }}
      />
      {children}
    </div>
  );
};
